import {useInvestmentPeriodsQuery} from '@transactions/graphql';
import {today} from 'common/date';
import {ChevronDown, ChevronRight} from 'lucide-react';
import * as React from 'react';
import {formatSigned, gainClass} from 'src/InvestmentAccountsView/money';
import {countedPeriods, partOf, sumPart} from 'src/InvestmentAccountsView/periodsModel';
import {cn} from 'src/lib/utils';
import {Segmented} from 'src/Segmented';
import type {DateTime} from 'luxon';
import type {Period, PeriodPart} from 'src/InvestmentAccountsView/periodsModel';

type Key = '1W' | '1M' | 'YTD' | '1Y' | 'All';

// A period runs from the close of its start day, so YTD starts at the
// prior year-end and All before any record.
const PERIODS: Array<{key: Key; label: string; start: (now: DateTime) => DateTime}> = [
  {key: '1W', label: '1W', start: (now) => now.minus({weeks: 1})},
  {key: '1M', label: '1M', start: (now) => now.minus({months: 1})},
  {key: 'YTD', label: 'YTD', start: (now) => now.startOf('year').minus({days: 1})},
  {key: '1Y', label: '1Y', start: (now) => now.minus({years: 1})},
  {key: 'All', label: 'All', start: (now) => now.set({year: 1900, month: 1, day: 1})},
];

/**
 * The parts of a period's change, in the order they add up; market results
 * are colored. Fees come out (sign −1), so they show as what they took.
 */
const PARTS: Array<{key: PeriodPart; label: string; gain: boolean; sign?: -1}> = [
  {key: 'saved', label: 'Saved', gain: false},
  {key: 'income', label: 'Income', gain: false},
  {key: 'fees', label: 'Fees', gain: false, sign: -1},
  {key: 'realized', label: 'Realized', gain: true},
  {key: 'unrealizedChange', label: 'Unrealized', gain: true},
  {key: 'unclassified', label: 'Unclassified', gain: true},
  {key: 'residualChange', label: 'Residual', gain: false},
];

// Every row is one grid with the same fixed column widths, so figures line
// up down the table. Columns appear as the card widens: change and
// realized always; unrealized next; saved and income; then unclassified and
// residual. A row's detail has them all.
const GRID =
  'grid grid-cols-[minmax(0,1fr)_5.75rem_5.75rem] items-center gap-x-3 @md:grid-cols-[minmax(0,1fr)_6rem_6rem_6rem] @3xl:grid-cols-[minmax(10rem,1fr)_6.5rem_6.5rem_6.5rem_6.5rem_6.5rem] @6xl:grid-cols-[minmax(12rem,1fr)_6.5rem_6.5rem_6.5rem_6.5rem_6.5rem_6.5rem_6.5rem]';
const NARROW = 'hidden text-right @md:block';
const MEDIUM = 'hidden text-right @3xl:block';
const WIDE = 'hidden text-right @6xl:block';

/** A signed amount, "—" when unknown; a gain colored, a partial sum muted. */
const Amount = ({
  cents,
  currency,
  gain = true,
  partial = false,
  className,
  as: Tag = 'span',
}: {
  cents: number | null;
  currency: string;
  gain?: boolean;
  partial?: boolean;
  className?: string;
  /** `dd` inside a description list. */
  as?: 'span' | 'dd';
}) => (
  <Tag
    className={cn(
      'text-right tabular-nums',
      partial ? 'text-muted-foreground' : gain && gainClass(cents),
      className,
    )}
  >
    {cents == null ? '—' : formatSigned(cents, currency)}
  </Tag>
);

const Cells = ({period}: {period: Period}) => (
  <>
    <Amount cents={period.balanceChange ?? null} currency={period.currency} />
    <Amount cents={period.realized ?? null} currency={period.currency} />
    <Amount cents={period.unrealizedChange ?? null} currency={period.currency} className={NARROW} />
    <Amount cents={period.saved ?? null} currency={period.currency} gain={false} className={MEDIUM} />
    <Amount cents={period.income ?? null} currency={period.currency} gain={false} className={MEDIUM} />
    <Amount cents={period.unclassified ?? null} currency={period.currency} className={WIDE} />
    <Amount cents={period.residualChange ?? null} currency={period.currency} gain={false} className={WIDE} />
  </>
);

const Row = ({period, name}: {period: Period; name: string}) => {
  const [open, setOpen] = React.useState(false);
  // Out of balance: a check moved. (Not exact for other reasons, like an
  // estimated sale or a missing price, isn't that; the detail says why.)
  const unbalanced = period.cashGap !== 0 || period.quantityGaps.length > 0;
  // A market part with no price is unknown though realized isn't.
  const unpriced =
    period.realizedUnknown == null &&
    [period.unrealizedChange, period.unclassified, period.residualChange].some((value) => value == null);
  return (
    <div className="border-border border-b last:border-b-0">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((state) => !state)}
        className={cn(GRID, 'w-full px-3 py-2.5 text-left text-sm')}
      >
        <span className="flex min-w-0 items-center gap-1.5">
          {open ? (
            <ChevronDown className="text-muted-foreground size-4 shrink-0" />
          ) : (
            <ChevronRight className="text-muted-foreground size-4 shrink-0" />
          )}
          <span className="flex min-w-0 flex-col">
            <span className="truncate font-medium">{name}</span>
            {unbalanced && <span className="text-warning text-xs">Not reconciled</span>}
          </span>
        </span>
        <Cells period={period} />
      </button>
      {open && (
        <dl className="bg-muted/40 grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 px-3 py-2 pl-8 text-xs">
          <dt className="text-muted-foreground">
            {period.from} → {period.to}
          </dt>
          <dd className="text-right tabular-nums">
            {period.balanceChange == null ? '—' : formatSigned(period.balanceChange, period.currency)}
          </dd>
          {PARTS.map(({key, label, gain, sign}) => {
            const cents = partOf(period, key);
            return (
              <React.Fragment key={key}>
                <dt className="text-muted-foreground">{label}</dt>
                <Amount
                  as="dd"
                  cents={cents == null ? null : cents * (sign ?? 1)}
                  currency={period.currency}
                  gain={gain}
                />
              </React.Fragment>
            );
          })}
          {(period.realizedUnknown != null || unpriced) && (
            <>
              <dt className="text-muted-foreground">Unknown</dt>
              <dd className="text-right">{period.realizedUnknown ?? 'price'}</dd>
            </>
          )}
          {period.writtenOff !== 0 && (
            <>
              <dt className="text-muted-foreground">Written off</dt>
              <Amount as="dd" cents={period.writtenOff} currency={period.currency} gain={false} />
            </>
          )}
          {period.cashGap !== 0 && (
            <>
              <dt className="text-muted-foreground">Cash gap</dt>
              <Amount as="dd" cents={period.cashGap} currency={period.currency} gain={false} />
            </>
          )}
          {period.quantityGaps.map((gap) => (
            <React.Fragment key={gap.securityId}>
              <dt className="text-muted-foreground">Share gap</dt>
              <dd className="text-right tabular-nums">
                {gap.quantity > 0 ? '+' : '−'}
                {Math.abs(gap.quantity).toLocaleString('en-US', {maximumSignificantDigits: 8})} sh ·{' '}
                {gap.label}
              </dd>
            </React.Fragment>
          ))}
        </dl>
      )}
    </div>
  );
};

/**
 * Each account's and company's change over a period, split into what was
 * saved, what it earned, and what the market did.
 */
export const InvestmentGains = ({accountNames}: {accountNames: Map<string, string>}) => {
  const [key, setKey] = React.useState<Key>('1M');
  const now = today();
  const period = PERIODS.find((p) => p.key === key) ?? PERIODS[1];
  const from = period.start(now).toISODate() ?? '';
  const to = now.toISODate() ?? '';
  // Derived from everything a sync or a setting can change, so it always
  // refreshes on mount, showing the cached figures meanwhile.
  const {data, loading, error} = useInvestmentPeriodsQuery({
    variables: {from, to},
    fetchPolicy: 'cache-and-network',
  });

  const periods = [...(data?.investmentPeriods ?? [])].sort(
    (a, b) => Math.abs(b.balanceChange ?? 0) - Math.abs(a.balanceChange ?? 0),
  );
  const nameOf = (p: Period) =>
    p.issuerName ?? (p.accountId != null ? (accountNames.get(p.accountId) ?? 'Account') : 'Account');
  const currencies = [...new Set(periods.map((p) => p.currency))].sort(
    (a, b) => Number(b === 'USD') - Number(a === 'USD'),
  );

  return (
    <section className="flex flex-col gap-3" aria-labelledby="gains-heading">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="gains-heading" className="text-base font-semibold">
          Gains
        </h2>
        <Segmented label="Gains period" options={PERIODS} value={key} onChange={setKey} />
      </div>
      {loading && data == null ? (
        <div className="text-muted-foreground p-4 text-sm">Loading gains...</div>
      ) : data == null && error != null ? (
        <div className="text-muted-foreground p-4 text-sm">Couldn&apos;t load gains.</div>
      ) : periods.length === 0 ? (
        <div className="text-muted-foreground p-4 text-sm">No holdings recorded in this period.</div>
      ) : (
        <div className="border-border bg-card @container rounded-lg border pt-2">
          <div className={cn(GRID, 'text-muted-foreground border-border border-b px-3 pb-2 text-xs')}>
            <span />
            <span className="text-right">Change</span>
            <span className="text-right">Realized</span>
            <span className={NARROW}>Unrealized</span>
            <span className={MEDIUM}>Saved</span>
            <span className={MEDIUM}>Income</span>
            <span className={WIDE}>Unclassified</span>
            <span className={WIDE}>Residual</span>
          </div>
          {currencies.map((currency) => {
            const mine = countedPeriods(periods).filter((p) => p.currency === currency);
            const total = (field: PeriodPart) => sumPart(mine, field);
            const cell = (field: PeriodPart, className?: string) => {
              const sum = total(field);
              return (
                <Amount
                  cents={sum.known === 0 ? null : sum.cents}
                  currency={currency}
                  gain={PARTS.find((part) => part.key === field)?.gain ?? true}
                  partial={!sum.complete}
                  className={cn('font-semibold', className)}
                />
              );
            };
            return (
              <div key={currency} className={cn(GRID, 'border-border border-b px-3 py-2.5 text-sm')}>
                <span className="pl-5 font-semibold">{currencies.length > 1 ? `Total ${currency}` : 'Total'}</span>
                {cell('balanceChange')}
                {cell('realized')}
                {cell('unrealizedChange', NARROW)}
                {cell('saved', MEDIUM)}
                {cell('income', MEDIUM)}
                {cell('unclassified', WIDE)}
                {cell('residualChange', WIDE)}
              </div>
            );
          })}
          {periods.map((p) => (
            <Row key={p.issuerId != null ? `issuer:${p.issuerId}` : `account:${p.accountId}`} period={p} name={nameOf(p)} />
          ))}
        </div>
      )}
    </section>
  );
};
